/**
 * "Send Pay Link" button for an admin-managed booking row.
 *
 * Visibility rules (mirrors the backend `_is_payable_booking` checks):
 *   • Booking is pending / unpaid (status ∈ pending, received, processing)
 *   • payment_mode ∈ pay_link | invoice (NOT paid / comp)
 *   • total > 0
 *   • Customer has an email
 * Disabled (but visible) when:
 *   • customer email missing → tooltip explains
 *
 * On click:
 *   1. POST /api/admin/bookings/{ref}/send-pay-link
 *   2. Switch to a success popover with:
 *      • Email status badges (customer + team)
 *      • Resend-DNS warning when email status==failed
 *      • Copy Pay Link button (always present so staff can paste into SMS)
 *      • Open in new tab
 */
import { useRef, useState } from "react";
import { Send, Copy, Check, ExternalLink, AlertTriangle, X, Loader2, BellOff, Bell } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { isBookingChasable } from "@/lib/bookingPaymentStatus";

// Phase 2L-A61 — eligibility now shares one canonical helper with the
// status-badge logic in /admin/bookings, so the green "Paid" pill and
// the Send / Resend / Enable Reminders buttons can never disagree.
// Previous (A56) version used `=== true` against `is_paid`, missing
// legacy rows where the field had crept in as `1` / `"true"`.
function isEligible(b) {
  return isBookingChasable(b);
}

export function SendPayLinkButton({ booking, onSent }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const popRef = useRef(null);

  if (!isEligible(booking)) return null;

  const hasEmail = !!booking?.customer?.email;
  const remindersSent = Number(booking?.payment_reminders_sent_count || 0);
  const remindersDisabled = !!booking?.payment_reminders_disabled;
  const lastPaylinkSent = booking?.payment_link_sent_at;
  const lastReminderAt = booking?.payment_reminder_last_sent_at;

  const toggleReminders = async () => {
    const url = remindersDisabled
      ? `/admin/bookings/${booking.reference}/enable-reminders`
      : `/admin/bookings/${booking.reference}/disable-reminders`;
    try {
      await api.post(url);
      toast.success(remindersDisabled ? "Reminders re-enabled" : "Reminders disabled");
      onSent?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not toggle reminders");
    }
  };

  const send = async () => {
    if (!hasEmail) {
      toast.error("Customer email missing — add one before sending");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${booking.reference}/send-pay-link`);
      setResult(data);
      // Defer the parent reload until the popover is closed so the popover
      // doesn't unmount mid-flight.
      // Surface email-status as a quick toast
      if (data.customer_email_status === "sent" && data.team_email_status === "sent") {
        toast.success(`Pay link sent · customer + team emailed`);
      } else if (data.customer_email_status === "failed") {
        toast.warning("Pay link created — customer email FAILED (likely Resend DNS not verified). Use Copy link below.");
      } else if (data.customer_email_status === "skipped_no_key") {
        toast.warning("Pay link created — Resend not configured. Use Copy link below.");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send pay link");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!result?.pay_link_url) return;
    try {
      await navigator.clipboard.writeText(result.pay_link_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Pay link copied — paste into SMS or chat");
    } catch (_) {
      toast.message(result.pay_link_url);
    }
  };

  // ============ Button + popover ============
  return (
    <div className="relative">
      {lastPaylinkSent && (
        <div className="text-[9px] uppercase tracking-wider font-bold text-slate-500 mb-0.5 leading-tight">
          <span title={`Pay link sent ${lastPaylinkSent.slice(0, 19).replace('T', ' ')}`}>
            Sent · {lastPaylinkSent.slice(5, 10).replace('-', '/')}
          </span>
          {remindersSent > 0 && (
            <span data-testid={`admin-booking-reminders-count-${booking.reference}`} className="ml-1.5 text-amber-700">
              · Reminder {remindersSent}/2
            </span>
          )}
          {remindersDisabled && <span className="ml-1.5 text-slate-400">· Reminders off</span>}
        </div>
      )}
      <div className="flex items-center gap-1">
        <button
          data-testid={`admin-booking-paylink-${booking.reference}`}
          onClick={send}
          disabled={busy || !hasEmail}
          title={!hasEmail ? "Customer email missing — add one to send a pay link" : "Send Stripe pay link to customer"}
          className="inline-flex items-center gap-1 px-2.5 h-8 rounded-lg text-xs font-semibold bg-cyan-600 hover:bg-cyan-700 text-white disabled:bg-slate-200 disabled:text-slate-500 disabled:cursor-not-allowed transition-colors"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          <span className="hidden sm:inline">{result ? "Resend" : lastPaylinkSent ? "Resend link" : "Send Pay Link"}</span>
        </button>
        {lastPaylinkSent && (
          <button
            data-testid={`admin-booking-toggle-reminders-${booking.reference}`}
            onClick={toggleReminders}
            title={remindersDisabled ? "Re-enable automatic reminders" : "Disable automatic reminders for this booking"}
            aria-label={remindersDisabled ? "Re-enable reminders" : "Disable reminders"}
            className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${remindersDisabled ? "bg-slate-200 text-slate-600 hover:bg-slate-300" : "bg-white border border-slate-200 text-slate-500 hover:bg-slate-50"}`}
          >
            {/* Feb 2026 — collapsed to icon-only to keep the Actions
                column narrow. Tooltip + aria-label preserve intent. */}
            {remindersDisabled ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>

      {result && (
        <div
          ref={popRef}
          data-testid={`admin-booking-paylink-popover-${booking.reference}`}
          className="absolute right-0 top-full mt-1 z-30 w-80 bg-white border border-slate-200 rounded-2xl shadow-xl p-4 text-left"
        >
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-700">Pay link generated</div>
              <div className="text-sm font-semibold text-slate-900">${Number(result.amount_due).toFixed(2)} · {booking.reference}</div>
            </div>
            <button onClick={() => { setResult(null); onSent?.(); }} className="p-1 rounded hover:bg-slate-100">
              <X className="w-3.5 h-3.5 text-slate-500" />
            </button>
          </div>

          {/* Email status badges */}
          <div className="flex flex-wrap gap-1.5 my-2 text-[10px] font-semibold uppercase tracking-wider">
            <StatusBadge label="Customer" status={result.customer_email_status} />
            <StatusBadge label="Team" status={result.team_email_status} />
          </div>

          {/* Failure / Resend-DNS hint */}
          {(result.customer_email_status !== "sent" || result.team_email_status !== "sent") && (
            <div className="mt-2 mb-2 bg-amber-50 border border-amber-200 rounded-lg p-2 flex items-start gap-2 text-[11px] text-amber-900 leading-relaxed">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <div>
                {result.customer_email_status === "skipped_no_key"
                  ? <>Resend isn't configured. Use <strong>Copy pay link</strong> below to send it manually.</>
                  : result.customer_email_status === "failed"
                    ? <>Email send failed — likely Resend DNS verification is still pending. The pay link is valid; use <strong>Copy pay link</strong> below.</>
                    : <>Team notification didn't send. The customer email went through; <strong>teamnoosa@pristinewater.net.au</strong> can be looped in manually if needed.</>
                }
                {result.customer_email_error && (
                  <div className="text-[10px] text-amber-700 mt-0.5 break-all font-mono">{result.customer_email_error}</div>
                )}
              </div>
            </div>
          )}

          {/* Pay link + copy + open */}
          <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 flex items-center gap-1">
            <span className="text-[11px] text-slate-600 truncate flex-1 font-mono" title={result.pay_link_url}>
              {result.pay_link_url}
            </span>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            <button
              data-testid={`admin-booking-paylink-copy-${booking.reference}`}
              onClick={copy}
              className="inline-flex items-center justify-center gap-1 text-xs font-semibold py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? "Copied" : "Copy pay link"}
            </button>
            <a
              href={result.pay_link_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-1 text-xs font-semibold py-1.5 rounded-lg border border-slate-300 hover:bg-slate-100 text-slate-700"
            >
              Open <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ label, status }) {
  const tone =
    status === "sent"            ? "bg-emerald-100 text-emerald-700" :
    status === "skipped_no_key"  ? "bg-slate-200 text-slate-600" :
                                   "bg-rose-100 text-rose-700";
  const text = status === "sent" ? "✓ sent" : status === "skipped_no_key" ? "skipped" : "✗ failed";
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded-full ${tone}`}>{label} {text}</span>
  );
}

export default SendPayLinkButton;
